import { usePage, router } from '@inertiajs/react';
import { useState } from 'react';
import DashboardLayout from '../../../../components/DashboardLayout';
import { Trash2 } from 'lucide-react';
import ScheduleModal from '@/components/admin/newsletter/ScheduleModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface Newsletter {
  id: number;
  name: string;
  slug: string;
}

interface Issue {
  id: number;
  subject: string;
  preheader: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  issue_number: number | null;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  ready_to_send: boolean;
  editable: boolean;
  recipient_count: number;
  tracked_posts_count: number;
  content: {
    id?: number;
    html_content: string;
    text_content: string;
  } | null;
}

interface TrackedPost {
  title: string;
  summary: string;
  url: string;
  published_at: string;
  author: string;
  reading_time: number;
}

interface Props {
  newsletter: Newsletter;
  issue: Issue;
  tracked_posts: TrackedPost[];
  [key: string]: any;
}

export default function Show() {
  const { newsletter, issue, tracked_posts = [] } = usePage<Props>().props;
  const [showPreview, setShowPreview] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const handleSendNow = () => {
    if (confirm('Are you sure you want to send this newsletter now?')) {
      router.post(`/admin/newsletters/${newsletter.id}/issues/${issue.id}/send_now`);
    }
  };

  const handleSendTest = (e: React.FormEvent) => {
    e.preventDefault();
    router.post(`/admin/newsletters/${newsletter.id}/issues/${issue.id}/send_test`, {
      newsletter_issue: { test_email: testEmail },
    }, {
      onSuccess: () => {
        setShowTestEmail(false);
        setTestEmail('');
        alert('Test email sent! Check your Rails logs.');
      },
    });
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this issue?')) {
      router.delete(`/admin/newsletters/${newsletter.id}/issues/${issue.id}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
      scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      sending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      sent: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    return styles[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const canEdit = issue.editable;
  const canSchedule = issue.editable;
  const canSend = issue.ready_to_send && issue.recipient_count > 0 && (issue.status === 'draft' || issue.status === 'scheduled');
  const canDelete = issue.status === 'draft';

  return (
    <DashboardLayout>
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <a
          href={`/admin/newsletters/${newsletter.id}/issues`}
          className="text-primary hover:text-primary/80"
        >
          ← Back to Issues
        </a>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Issue #{issue.issue_number}: {issue.subject}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(issue.status)}`}>
              {issue.status}
            </span>
            <span className="text-sm text-muted-foreground">
              Created {formatDate(issue.created_at)}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          {canSend && (
            <button
              onClick={handleSendNow}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Send Now
            </button>
          )}
          {canSchedule && (
            <button
              onClick={() => setShowSchedule(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {issue.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
            </button>
          )}
          {canEdit && (
            <a
              href={`/admin/newsletters/${newsletter.id}/issues/${issue.id}/edit`}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
            >
              Edit
            </a>
          )}
          {canDelete && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-card text-card-foreground p-4 rounded-lg shadow border">
          <div className="text-sm text-muted-foreground">Recipients</div>
          <div className="text-2xl font-bold">{issue.recipient_count}</div>
        </div>
        <div className="bg-card text-card-foreground p-4 rounded-lg shadow border">
          <div className="text-sm text-muted-foreground">Tracked Posts</div>
          <div className="text-2xl font-bold">{tracked_posts.length}</div>
        </div>
        <div className="bg-card text-card-foreground p-4 rounded-lg shadow border">
          <div className="text-sm text-muted-foreground">Status</div>
          <div className="text-2xl font-bold capitalize">{issue.status}</div>
        </div>
      </div>

      {/* Schedule Info */}
      {(issue.scheduled_at || issue.sent_at) && (
        <div className="mb-6 p-4 bg-muted rounded-lg">
          {issue.scheduled_at && (
            <p className="text-sm text-muted-foreground">
              Scheduled for: <strong>{formatDate(issue.scheduled_at)}</strong>
            </p>
          )}
          {issue.sent_at && (
            <p className="text-sm text-muted-foreground">
              Sent at: <strong>{formatDate(issue.sent_at)}</strong>
            </p>
          )}
        </div>
      )}

      {/* Content */}
      <div className="bg-card text-card-foreground rounded-lg shadow border p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Content</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowTestEmail(true)}
              className="px-3 py-1 text-sm bg-muted text-foreground rounded hover:bg-muted/80 transition-colors"
            >
              Send Test
            </button>
            <button
              onClick={() => setShowPreview(true)}
              className="px-3 py-1 text-sm bg-muted text-foreground rounded hover:bg-muted/80 transition-colors"
            >
              Preview
            </button>
          </div>
        </div>
        
        {issue.content?.html_content ? (
          <div 
            className="prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: issue.content.html_content }}
          />
        ) : (
          <p className="text-muted-foreground italic">No content yet.</p>
        )}
      </div>

      {/* Tracked Posts */}
      {tracked_posts.length > 0 && (
        <div className="bg-card text-card-foreground rounded-lg shadow border p-6">
          <h2 className="text-lg font-medium mb-4">Included Blog Posts</h2>
          <div className="space-y-4">
            {tracked_posts.map((post, index) => (
              <div key={index} className="flex items-start p-4 bg-muted rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium">{post.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{post.summary}</p>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground/70">
                    <span>By {post.author}</span>
                    <span>{post.reading_time} min read</span>
                    <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      View Post →
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview - {issue.subject}</DialogTitle>
            <DialogDescription>
              This is how your newsletter will appear to recipients.
            </DialogDescription>
          </DialogHeader>
          <div 
            className="prose dark:prose-invert max-w-none p-4 border border-border rounded-lg bg-muted"
            dangerouslySetInnerHTML={{ __html: issue.content?.html_content || '' }}
          />
        </DialogContent>
      </Dialog>

      {/* Schedule Modal */}
      <ScheduleModal
        open={showSchedule}
        onOpenChange={setShowSchedule}
        newsletterId={newsletter.id}
        issueId={issue.id}
        status={issue.status}
        scheduledAt={issue.scheduled_at}
        editable={issue.editable}
      />

      {/* Test Email Modal */}
      <Dialog open={showTestEmail} onOpenChange={setShowTestEmail}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Enter an email address to receive a test version of this newsletter.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendTest}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="test-email" className="text-sm font-medium">Email Address</label>
                <input
                  id="test-email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                  placeholder="you@example.com"
                  required
                />
              </div>
              
              <p className="text-sm text-muted-foreground">
                The test email will be logged to your Rails console.
              </p>
            </div>
            <DialogFooter>
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              >
                Send Test
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
